import * as React from 'react';
import { styled } from '@linaria/react';
import { SandpackThemeProvider } from '@codesandbox/sandpack-react';

import { BREAKPOINTS } from '@/constants';

import Space from '@/components/Space';
import { BaseWrapper as AsideWrapper } from '@/components/Aside';

import theme from './theme';
import useSaveShortcut from './use-save-shortcut.hook';

interface Props {
  id: string;
  stacked?: boolean;
  className?: string;
  isFullscreened?: boolean;
  children: React.ReactNode;
  ref: React.RefObject<HTMLDivElement>;
}

function CodeWrapper({
  id,
  stacked,
  className,
  isFullscreened,
  children,
  ref,
}: Props) {
  const handleSave = useSaveShortcut();

  return (
    <InlineOuterWrapper
      ref={ref}
      data-id={id}
      data-can-be-annotated={false}
      data-stacked={String(!!stacked)}
      onKeyDown={handleSave}
    >
      <Wrapper
        className={className}
        data-is-fullscreened={isFullscreened}
      >
        <SandpackThemeProvider theme={theme}>
          {children}
        </SandpackThemeProvider>
      </Wrapper>
    </InlineOuterWrapper>
  );
}

export const InlineOuterWrapper = styled.div`
  margin-top: 16px;
  margin-bottom: 48px;

  @media ${BREAKPOINTS.mdAndSmaller} {
    margin-bottom: 32px;
  }

  &[data-stacked='true'] {
    /* When stacked, lower margin on both mobile and desktop. */
    margin-bottom: 16px;
  }

  /* Asides have a 3px border along the left, and on very small screens, the playground was overlapping and covering this border. Add a bit of margin to prevent this. */
  @media ${BREAKPOINTS.smAndSmaller} {
    ${AsideWrapper} & {
      margin-left: 3px;
    }
  }
`;

const Wrapper = styled(Space)`
  background: var(--syntax-bg);
  border: none;

  &[data-is-fullscreened='false'] {
    position: relative;
    z-index: 1;
    width: calc(100% + 16px * 2);
    margin-inline: calc(16px * -1);
    border-radius: 6px;

    /*
      If we're in space, the background color "blends in", in
      a not-very-nice way. We'll add a border, which will be
      removed if we're rendering in Paper or an Aside
    */
    outline: 1px solid var(--color-gray-100);

    @media ${BREAKPOINTS.smAndSmaller} {
      border-radius: 0;
      outline: none;
      border-bottom: 2px solid var(--color-gray-100);
    }
  }

  &[data-is-fullscreened='true'] {
    position: fixed;
    z-index: 2;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    display: flex;
    flex-direction: column;

    & .sp-wrapper {
      /* Grow to fill the full height */
      flex: 1;

      /*
        Don't expand beyond the window height.
        Because the right children have “overflow: auto”,  this
        is all that's needed.
      */
      height: 100%;
    }
  }

  /* Override built-in Sandpack / CodeMirror styles */
  .sp-cm {
    padding-top: 16px;
    padding-bottom: 8px;
    animation: fadeIn 125ms;
  }

  .sp-code-editor {
    scrollbar-width: thin;
  }

  .sp-tabs {
    border-bottom-color: var(--color-gray-100);
  }

  .sp-stack {
    height: 100%;
  }

  .sp-cm:focus-visible .cm-editor {
    outline: 2px solid var(--color-primary);
    outline-offset: -2px;
    border-radius: 4px;
  }
  .cm-editor * {
    line-height: 1.5;
  }
  .cm-content {
    padding: 0px 8px !important;
  }
  .cm-scroller {
    overflow: visible !important;
    padding: 0px !important;
  }

  .cm-line {
    padding: 0 8px !important;
  }
  .cm-activeLine {
    background-color: hsl(210deg 15% 50% / 0.165) !important;
  }

  .cm-gutters {
    margin-right: -4px;
  }

  .cm-lineNumbers {
    font-size: 0.9em !important;
    color: var(--color-gray-500);
  }

  .sp-pre-placeholder {
    /*
      NOTE: In the past, I was hiding this placeholder with
      display: none;. That reduced any stuttering caused by
      inconsistencies between the placeholder and the actual
      content.

      For the best SSR experience, though, I do want to show
      this. And so I've tweaked the margin/padding to match.

      IF I run into issues, I can replace these declarations
      with 'display: none'.
    */
    margin: 0 !important;
    padding: 0px 16px !important;
    line-height: 1.5;
  }

  /* Restore old tab styles */
  .sp-tab-button {
    border-bottom: 1px solid transparent;
    transition: border-bottom 150ms ease-out;
  }
  .sp-tab-button[data-active='true'] {
    color: white;
    border-bottom: 1px solid var(--sp-colors-accent);
  }
  .sp-tab-button:focus-visible {
    outline: 2px auto var(--color-primary);
    outline-offset: -2px;
    color: var(--sp-colors-fg-active);
  }
  .sp-tab-button[data-active='true']:focus-visible {
    border-bottom: 1px solid transparent;
  }
`;

export const FullscreenWrapper = styled(Wrapper)``;

export const InlineWrapper = styled(Wrapper)``;

export default CodeWrapper;
